import React, { useEffect, useRef, useState } from "react";
import MelhorarSkills from "./MelhorarSkills";
import {
  vagasSemelhantes, obterVaga, candidatarVaga, declararCandidatura, obterMeuPerfil, salvarPerfil,
  passoFunilVaga, resumoDoLote,
} from "../services/api";
import { noSiteDaEmpresa } from "../utils/candidatura";
import { rotulo } from "../utils/rotulos";

/**
 * 🔎 CANDIDATAR-SE A VÁRIAS VAGAS DE UMA VEZ — ideia do Nelson em 29/09/2026, mockup aprovado em
 * mokups/candidatura-em-lote.html.
 *
 * Por que existe: o visitante lia UMA vaga e ia embora (o monitor mostrava isso o dia inteiro). E o post
 * mostra 5 vagas de um tipo que tem centenas no mural — quem chega por ele (?de=rpa) vê aqui as outras
 * DAQUELE tipo, ordenadas pelo match com o currículo dele.
 *
 * 🧭 OS DOIS FLUXOS DE HOJE FICAM INTACTOS. O carrossel chama as MESMAS funções da API (candidatarVaga /
 * declararCandidatura) e reaproveita o ModalEntrega, que narra a entrega e já cuida do "o ATS recusou,
 * vai pelo site". Mexer em MeCandidatar/CandidaturaNoSite seria mexer no caminho mais crítico do produto.
 *
 * Regras combinadas com o Nelson:
 *   • só vagas com match ≥ 50% (o servidor corta), no máximo 10 marcadas por vez;
 *   • cada vaga diz COMO a candidatura acontece (✅ pelo eaiRH / 🔗 no site da empresa);
 *   • vaga do site da empresa SEGURA A FILA até a pessoa voltar e dizer se conseguiu;
 *   • UM e-mail no fim, com o lote separado por tipo (a confirmação individual não sai no lote);
 *   • fora da lista: a própria vaga, as que ela já se candidatou e as que já abriu no site (o servidor tira).
 */
const LIMITE = 10;
const tipoDe = (v) => (noSiteDaEmpresa(v) ? "site" : "direta");
// Canal de contato (e-mail/WhatsApp divulgado no anúncio) não tem como ir no lote: fica fora da lista.
const cabeNoLote = (v) => !(v.fonte === "Externa" && !noSiteDaEmpresa(v));

function Etiqueta({ tipo }) {
  return tipo === "site"
    ? <span className="inline-block rounded-md bg-sky-50 px-2 py-0.5 text-[11.5px] font-bold text-sky-700">🔗 No site da empresa</span>
    : <span className="inline-block rounded-md bg-green-50 px-2 py-0.5 text-[11.5px] font-bold text-green-700">✅ Candidatura pelo eaiRH</span>;
}

function Chips({ tem = [], faltando = [] }) {
  return (
    <div className="my-1.5 flex flex-wrap gap-1.5">
      {tem.map((s) => <span key={"t" + s} className="rounded-full bg-green-50 px-2.5 py-0.5 text-xs text-green-800">✓ {s}</span>)}
      {faltando.map((s) => <span key={"f" + s} className="rounded-full bg-orange-50 px-2.5 py-0.5 text-xs text-orange-700">✗ {s}</span>)}
    </div>
  );
}

export default function CandidaturaEmLote({ vaga, de, aberto, onFechar, ModalEntrega }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [sel, setSel] = useState([]);                 // ids, na ordem em que foram marcados
  const [expandidas, setExpandidas] = useState({});
  const [tela, setTela] = useState("lista");          // lista | completa | carrossel | resumo
  const [completa, setCompleta] = useState(null);     // vaga inteira (GET /vagas/{id})
  const [cursosDe, setCursosDe] = useState(null);     // vaga cujo "como aprimorar" está aberto
  const rolagem = useRef(0);
  const caixa = useRef(null);

  // carrossel
  const [fila, setFila] = useState([]);
  const [pos, setPos] = useState(0);
  const [fase, setFase] = useState("form");           // direta: form | entrega · site: acordo | voltou
  const [mensagem, setMensagem] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [resultados, setResultados] = useState([]);   // {id, r: direta|site|pulada|naoconseguiu}
  const [perfil, setPerfil] = useState(undefined);
  const [pretensao, setPretensao] = useState("");
  const [pulouPretensao, setPulouPretensao] = useState(false);
  const resumoEnviado = useRef(false);

  useEffect(() => {
    if (!aberto || !vaga?.id) return;
    setDados(null); setErro(null); setSel([]); setTela("lista"); setResultados([]); resumoEnviado.current = false;
    passoFunilVaga(vaga.id, "lote_abriu");
    vagasSemelhantes(vaga.id, de)
      .then((r) => setDados({ ...r, itens: (r.itens || []).filter(cabeNoLote) }))
      .catch((e) => setErro(e.message || "Não consegui carregar as vagas agora."));
    obterMeuPerfil().then(setPerfil).catch(() => setPerfil(null));
  }, [aberto, vaga?.id, de]);

  if (!aberto) return null;

  const itens = dados?.itens || [];
  const marcar = (id) => {
    setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length >= LIMITE ? s : [...s, id]));
  };
  const abrirCompleta = async (v) => {
    rolagem.current = caixa.current?.scrollTop || 0;
    setCompleta({ ...v, carregando: true }); setTela("completa");
    try { const full = await obterVaga(v.id); setCompleta({ ...v, ...full, carregando: false }); }
    catch { setCompleta({ ...v, carregando: false }); }
    if (caixa.current) caixa.current.scrollTop = 0;
  };
  const voltarLista = (destacar) => {
    setTela("lista");
    setTimeout(() => {
      if (caixa.current) caixa.current.scrollTop = rolagem.current;
      const el = destacar && document.getElementById("lote-" + destacar);
      if (el) { el.scrollIntoView({ block: "center" }); el.animate?.([{ boxShadow: "0 0 0 4px #FB8500" }, { boxShadow: "0 0 0 0 #FB8500" }], { duration: 1200 }); }
    }, 30);
  };

  // ───────── carrossel
  const iniciar = () => {
    const f = sel.map((id) => itens.find((x) => x.id === id)).filter(Boolean);
    if (!f.length) return;
    passoFunilVaga(vaga.id, "lote_enviou");
    setFila(f); setPos(0); setResultados([]); setMensagem("");
    setFase(tipoDe(f[0]) === "site" ? "acordo" : "form"); setTela("carrossel");
  };
  const proxima = (r) => {
    const atual = fila[pos];
    const novos = [...resultados, { id: atual.id, r }];
    setResultados(novos); setMensagem(""); setEnviando(false); setErro(null);
    if (pos + 1 < fila.length) {
      const prox = fila[pos + 1];
      setPos(pos + 1); setFase(tipoDe(prox) === "site" ? "acordo" : "form");
    } else fecharLote(novos);
  };
  const fecharLote = (res) => {
    // O que não foi decidido vira "pendente" — nunca some calado.
    const ja = new Set(res.map((x) => x.id));
    const tudo = [...res, ...fila.filter((v) => !ja.has(v.id)).map((v) => ({ id: v.id, r: "pulada" }))];
    setResultados(tudo); setTela("resumo");
    if (!resumoEnviado.current && tudo.some((x) => x.r === "direta" || x.r === "site" || x.r === "pulada")) {
      resumoEnviado.current = true;
      resumoDoLote({
        enviadas: tudo.filter((x) => x.r === "direta").map((x) => x.id),
        noSite: tudo.filter((x) => x.r === "site").map((x) => x.id),
        pendentes: tudo.filter((x) => x.r === "pulada" || x.r === "naoconseguiu").map((x) => x.id),
      }).catch(() => { /* o resumo por e-mail não pode travar a tela */ });
    }
  };
  const fechar = () => {
    if (tela === "carrossel" && !window.confirm("Sair agora? As vagas que faltam ficam no resumo como pendentes.")) return;
    if (tela === "carrossel") { fecharLote(resultados); return; }
    onFechar && onFechar();
  };

  const atual = fila[pos];
  const semPretensao = !!perfil && !perfil.pretensaoClt && !perfil.pretensaoPj;

  // Enquanto o ModalEntrega narra, ele é a única coisa na tela (é um overlay próprio).
  if (tela === "carrossel" && atual && fase === "entrega" && ModalEntrega)
    return <ModalEntrega vagaId={atual.id} onFim={() => proxima("direta")} />;

  const Progresso = () => (
    <div className="mt-2 flex gap-1.5">{fila.map((_, i) => (
      <i key={i} className={`h-1.5 flex-1 rounded ${i < pos ? "bg-green-600" : i === pos ? "bg-orange-500" : "bg-gray-200"}`} />))}</div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-3" onClick={(e) => { if (e.target === e.currentTarget) fechar(); }}>
      <div ref={caixa} className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-auto rounded-2xl bg-white shadow-2xl">

        {/* ───────── LISTA ───────── */}
        {tela === "lista" && (<>
          <div className="sticky top-0 z-10 border-b bg-white px-5 pb-3 pt-4">
            <button onClick={fechar} aria-label="Fechar" className="float-right text-2xl leading-none text-gray-400 hover:text-gray-700">×</button>
            <h3 className="text-lg font-bold text-primary">{dados?.tituloReceita ? `Mais ${dados.tituloReceita} para você` : "Vagas semelhantes a esta"}</h3>
            <p className="text-sm text-gray-600">
              {dados?.tituloReceita
                ? <>Do mesmo tipo do post que te trouxe aqui{dados.totalDoTipo ? <> ({dados.totalDoTipo} no mural)</> : null}, ordenadas pelo match com o seu currículo.</>
                : <>Parecidas com <b>{vaga.titulo}</b>, ordenadas pelo match com o seu currículo.</>}
            </p>
          </div>
          <div className="px-5 py-3">
            {erro && <p className="text-sm text-red-600">{erro}</p>}
            {!dados && !erro && <p className="py-6 text-sm text-gray-500">Procurando as vagas que combinam com você…</p>}
            {dados?.semCurriculo && <p className="py-4 text-sm text-gray-700">Preencha seu currículo para vermos quais vagas combinam com você. <a href="/curriculo" className="font-semibold text-primary underline">Preencher agora</a></p>}
            {dados && !dados.semCurriculo && itens.length === 0 && <p className="py-4 text-sm text-gray-600">Não achamos outras vagas com match de 50% ou mais para você agora. Volte amanhã — entram vagas novas todo dia.</p>}
            {itens.length > 0 && (
              <p className="mb-3 rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-600">
                <Etiqueta tipo="direta" /> a gente envia seu currículo por você · <Etiqueta tipo="site" /> você conclui no site da empresa (a gente abre e espera você voltar)
              </p>
            )}
            {itens.map((v) => {
              const marcada = sel.includes(v.id), aberta = !!expandidas[v.id];
              const trava = v.obrigatoriasFaltando?.length ? v.obrigatoriasFaltando : null;   // 🔒 20/10: skill obrigatória que ele não tem
              return (
                <div key={v.id} id={"lote-" + v.id} className={`mb-2 flex gap-3 rounded-xl border p-3 ${marcada ? "border-orange-400 bg-orange-50/40" : "border-gray-200"}`}>
                  <input type="checkbox" checked={marcada && !trava} onChange={() => marcar(v.id)} disabled={!!trava || (!marcada && sel.length >= LIMITE)}
                    title={trava ? `Esta vaga exige ${trava.join(", ")}, que não está no seu currículo` : !marcada && sel.length >= LIMITE ? `Limite de ${LIMITE} vagas por vez` : undefined}
                    className="mt-1 h-[18px] w-[18px] accent-orange-500 disabled:opacity-40" aria-label={`Selecionar ${v.titulo}`} />
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-gray-900">{v.titulo}</div>
                    <div className="text-xs text-gray-500">{v.empresa}{v.cidade ? ` · ${v.cidade}` : ""}{v.modalidade ? ` · ${rotulo(v.modalidade)}` : ""}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span className="rounded-md bg-blue-50 px-2 py-0.5 text-[11.5px] font-bold text-primary">{v.match}% de match</span>
                      <Etiqueta tipo={tipoDe(v)} />
                      {trava && <span className="rounded-md bg-red-50 px-2 py-0.5 text-[11.5px] font-bold text-red-700">🔒 exige {trava.join(", ")}</span>}
                    </div>
                    {trava && (
                      <p className="mt-1 text-xs text-red-800">Sem {trava.join(", ")} no currículo a empresa recusa na triagem.{" "}
                        <button type="button" onClick={() => setCursosDe({ ...v, matchFaltando: trava })} className="font-semibold underline">Como desenvolver →</button>
                        {" · "}<button type="button" onClick={() => abrirCompleta(v)} className="font-semibold underline">Tenho, quero me candidatar</button></p>
                    )}
                    {aberta && (
                      <div className="mt-2 border-t border-dashed pt-2 text-[13px] text-gray-700">
                        <b>Skills que a vaga pede</b>
                        <Chips tem={v.matchTem} faltando={v.matchFaltando} />
                        {v.descricao && <p className="text-gray-600">{v.descricao}</p>}
                        <div className="my-1.5 flex flex-wrap gap-x-4 gap-y-1 text-gray-700">
                          {v.tipoContratacao && <span>📄 {rotulo(v.tipoContratacao)}</span>}
                          <span>💰 {v.salarioMin || v.salarioMax ? `R$ ${v.salarioMin || ""}${v.salarioMax ? ` a ${v.salarioMax}` : ""}` : "Salário não informado"}</span>
                          <span>⏱️ {v.prazoDeclaradoPelaEmpresa ? `Retorno em até ${v.respostaPrazoDias} dias` : "Prazo não declarado"}</span>
                        </div>
                        <div className="my-1.5 rounded-lg bg-blue-50 px-2.5 py-1.5 text-xs">🏢 Reputação da empresa: {v.empresaClassificacao || "em formação"}</div>
                        <button type="button" onClick={() => abrirCompleta(v)} className="font-semibold text-primary underline">Ver vaga completa →</button>
                      </div>
                    )}
                  </div>
                  <button type="button" title="Ver detalhes" onClick={() => setExpandidas((e) => ({ ...e, [v.id]: !e[v.id] }))}
                    className={`h-8 w-8 shrink-0 rounded-lg bg-slate-100 text-primary transition-transform ${aberta ? "rotate-180" : ""}`}>▼</button>
                </div>
              );
            })}
            {itens.length > 0 && <p className="mt-2 text-xs text-gray-500">Mostramos só vagas com match a partir de 50%. Dá para marcar até {LIMITE} por vez — candidatura em massa vira spam para a empresa e prejudica você.</p>}
          </div>
          {itens.length > 0 && (
            <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t bg-white px-5 py-3">
              <span className="text-sm text-gray-600">
                {sel.length >= LIMITE
                  ? <span className="font-semibold text-orange-700">Você chegou ao limite de {LIMITE} vagas por vez</span>
                  : <><b>{sel.length}</b> selecionada{sel.length === 1 ? "" : "s"}</>}
              </span>
              <button disabled={!sel.length} onClick={iniciar}
                className="rounded-xl bg-orange-600 px-5 py-2.5 font-semibold text-white hover:bg-orange-700 disabled:opacity-45">
                {sel.length ? `Candidatar-me às ${sel.length} selecionadas →` : "Candidatar-me às selecionadas →"}
              </button>
            </div>
          )}
        </>)}

        {/* ───────── VAGA COMPLETA (no mesmo modal: nada de modal sobre modal) ───────── */}
        {tela === "completa" && completa && (<>
          <div className="sticky top-0 z-10 border-b bg-white px-5 pb-3 pt-4">
            <button onClick={() => voltarLista()} className="mb-2 rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700">← Voltar à lista</button>
            <h3 className="text-lg font-bold text-gray-900">{completa.titulo}</h3>
            <div className="text-sm text-gray-500">{completa.empresa}{completa.cidade ? ` · ${completa.cidade}` : ""}</div>
            <div className="mt-1 flex flex-wrap gap-1.5"><span className="rounded-md bg-blue-50 px-2 py-0.5 text-[11.5px] font-bold text-primary">{completa.match}% de match</span><Etiqueta tipo={tipoDe(completa)} /></div>
          </div>
          <div className="px-5 py-3 text-sm text-gray-700">
            <div className="mb-2 rounded-lg bg-blue-50 px-3 py-2 text-xs">🏢 <b>Reputação da empresa:</b> {completa.empresaClassificacao || "em formação"}</div>
            <h4 className="mt-3 font-bold text-gray-900">Skills que a vaga pede</h4>
            <Chips tem={completa.matchTem} faltando={completa.matchFaltando} />
            {completa.matchFaltando?.length > 0 && (
              <p className="text-xs text-gray-500">✗ = ainda não está no seu currículo · <button type="button" onClick={() => setCursosDe(completa)} className="font-semibold text-primary underline">como aprimorar →</button></p>
            )}
            <h4 className="mt-3 font-bold text-gray-900">Sobre a vaga</h4>
            {completa.carregando ? <p className="text-gray-500">Carregando…</p>
              : <div className="whitespace-pre-line leading-relaxed">{completa.descricao}</div>}
          </div>
          <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t bg-white px-5 py-3">
            <button onClick={() => voltarLista()} className="rounded-xl bg-gray-100 px-4 py-2.5 font-semibold text-gray-700">← Voltar à lista</button>
            <button disabled={!sel.includes(completa.id) && sel.length >= LIMITE} onClick={() => { marcar(completa.id); voltarLista(completa.id); }}
              className={`rounded-xl px-5 py-2.5 font-semibold ${sel.includes(completa.id) ? "bg-gray-100 text-gray-700" : "bg-orange-600 text-white hover:bg-orange-700"}`}>
              {sel.includes(completa.id) ? "Desmarcar esta vaga" : "✓ Selecionar esta vaga"}
            </button>
          </div>
        </>)}

        {/* ───────── CARROSSEL ───────── */}
        {tela === "carrossel" && atual && (<>
          <div className="sticky top-0 z-10 border-b bg-white px-5 pb-3 pt-4">
            <button onClick={fechar} aria-label="Fechar" className="float-right text-2xl leading-none text-gray-400 hover:text-gray-700">×</button>
            <div className="text-sm text-gray-500">Vaga <b>{pos + 1} de {fila.length}</b></div>
            <Progresso />
            <h3 className="mt-2 text-lg font-bold text-gray-900">{atual.titulo}</h3>
            <div className="text-sm text-gray-500">{atual.empresa} · <Etiqueta tipo={tipoDe(atual)} /></div>
          </div>
          <div className="px-5 py-4 text-sm text-gray-700">
            {erro && <p className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-800">{erro}</p>}

            {/* PELO eaiRH — pretensão pedida UMA vez (vai para o perfil e vale para as próximas) */}
            {tipoDe(atual) === "direta" && fase === "form" && semPretensao && !pulouPretensao && (
              <div className="rounded-xl border border-sky-200 bg-sky-50 p-3">
                <p className="font-bold text-sky-900">Qual a sua pretensão salarial?</p>
                <p className="mt-1 text-sky-800">Algumas empresas recusam a candidatura sem ela. Você responde uma vez e vale para todas as vagas do lote.</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <input type="number" min="1" inputMode="numeric" value={pretensao} onChange={(e) => setPretensao(e.target.value)} placeholder="R$ por mês" className="w-40 rounded-xl border border-sky-300 px-3 py-2" />
                  <button disabled={!(Number(pretensao) > 1)} onClick={async () => {
                    try { await salvarPerfil({ ...perfil, pretensaoClt: Number(pretensao) }); setPerfil({ ...perfil, pretensaoClt: Number(pretensao) }); }
                    catch { setPulouPretensao(true); }
                  }} className="rounded-xl bg-orange-600 px-4 py-2 font-semibold text-white disabled:opacity-50">Salvar e continuar</button>
                  <button onClick={() => setPulouPretensao(true)} className="text-sky-800 underline">Prefiro não informar</button>
                </div>
              </div>
            )}
            {tipoDe(atual) === "direta" && fase === "form" && !(semPretensao && !pulouPretensao) && (<>
              <label className="block font-semibold text-gray-700">Quer contar algo à empresa? <span className="font-normal text-gray-400">(opcional)</span></label>
              <textarea value={mensagem} onChange={(e) => setMensagem(e.target.value)} rows={3} maxLength={1000}
                placeholder="Um diferencial, o motivo de querer esta vaga, disponibilidade…" className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
              <p className="mt-1 text-xs text-gray-500">Seu currículo vai junto. A empresa lê isto antes de abri-lo.</p>
            </>)}

            {/* NO SITE DA EMPRESA — a fila só anda quando a pessoa volta */}
            {tipoDe(atual) === "site" && fase === "acordo" && (<>
              <p>Esta empresa recebe candidaturas <b>no site dela</b>. Vamos abrir a vaga em outra aba.</p>
              <p className="mt-2">Em troca, pedimos uma coisa: <b>volte aqui e conte</b> se conseguiu — é assim que o eaiRH acompanha se a empresa dá retorno.</p>
            </>)}
            {tipoDe(atual) === "site" && fase === "voltou" && (
              <div className="rounded-xl bg-blue-50 p-4 text-center">
                <p className="font-bold text-gray-900">Aguardando você voltar do site da empresa…</p>
                <p className="mt-1 text-gray-600">A vaga abriu em outra aba. Quando terminar lá, conte como foi:</p>
              </div>
            )}
          </div>
          <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-2 border-t bg-white px-5 py-3">
            <button onClick={() => proxima("pulada")} className="rounded-xl bg-gray-100 px-4 py-2.5 font-semibold text-gray-700">Pular esta</button>
            {tipoDe(atual) === "direta" && fase === "form" && !(semPretensao && !pulouPretensao) && (
              <button disabled={enviando} onClick={async () => {
                setEnviando(true); setErro(null);
                try { await candidatarVaga(atual.id, mensagem, true); setFase("entrega"); }
                catch (e) { if (e?.jaCandidatado) { setFase("entrega"); return; } setErro(e.message || "Não consegui enviar agora."); setEnviando(false); }
              }} className="rounded-xl bg-orange-600 px-5 py-2.5 font-semibold text-white hover:bg-orange-700 disabled:opacity-60">
                {enviando ? "Enviando…" : "Enviar candidatura"}
              </button>
            )}
            {tipoDe(atual) === "site" && fase === "acordo" && (
              <button onClick={() => {
                // Abre ANTES de registrar: falha nossa não pode impedir a candidatura dela.
                window.open(atual.urlCandidaturaExterna || atual.urlExterna, "_blank", "noopener,noreferrer");
                declararCandidatura(atual.id, { abriu: true }).catch(() => {});
                setFase("voltou");
              }} className="rounded-xl bg-orange-600 px-5 py-2.5 font-semibold text-white hover:bg-orange-700">Concordo, abrir a vaga ↗</button>
            )}
            {tipoDe(atual) === "site" && fase === "voltou" && (
              <div className="flex flex-wrap gap-2">
                <button disabled={enviando} onClick={async () => {
                  setEnviando(true);
                  try { await declararCandidatura(atual.id, { naoConsegui: true }); } catch { /* segue */ }
                  proxima("naoconseguiu");
                }} className="rounded-xl bg-gray-100 px-4 py-2.5 font-semibold text-gray-700">Não consegui</button>
                <button disabled={enviando} onClick={async () => {
                  setEnviando(true); setErro(null);
                  try { await declararCandidatura(atual.id, { naoConsegui: false }); proxima("site"); }
                  catch (e) { setErro(e.message || "Não consegui registrar. Tente de novo."); setEnviando(false); }
                }} className="rounded-xl bg-primary px-5 py-2.5 font-semibold text-white">✔ Concluí a candidatura</button>
              </div>
            )}
          </div>
        </>)}

        {/* ───────── RESUMO ───────── */}
        {tela === "resumo" && (() => {
          const n = (k) => resultados.filter((x) => x.r === k).length;
          const pend = n("pulada") + n("naoconseguiu");
          return (<>
            <div className="border-b px-5 pb-3 pt-4">
              <button onClick={() => onFechar && onFechar()} aria-label="Fechar" className="float-right text-2xl leading-none text-gray-400 hover:text-gray-700">×</button>
              <h3 className="text-lg font-bold text-gray-900">Pronto! 🎉</h3>
              <p className="text-sm text-gray-500">Resumo deste lote</p>
            </div>
            <div className="px-5 py-4">
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div className="rounded-xl bg-green-50 p-3"><b className="block text-2xl text-green-700">{n("direta")}</b>enviadas pelo eaiRH</div>
                <div className="rounded-xl bg-sky-50 p-3"><b className="block text-2xl text-sky-700">{n("site")}</b>concluídas no site da empresa</div>
                <div className="rounded-xl bg-gray-100 p-3"><b className="block text-2xl text-gray-500">{pend}</b>ficaram para depois</div>
              </div>
              <p className="mt-3 text-sm text-gray-700">📧 Mandamos <b>um e-mail só</b> com este resumo, o link para contar como foi cada processo e os links das que ficaram para depois.</p>
            </div>
            <div className="flex justify-end gap-2 border-t px-5 py-3">
              <a href="/minhas-candidaturas" className="rounded-xl border border-gray-300 px-4 py-2.5 font-semibold text-gray-700">Minhas candidaturas</a>
              <button onClick={() => onFechar && onFechar()} className="rounded-xl bg-primary px-5 py-2.5 font-semibold text-white">Fechar</button>
            </div>
          </>);
        })()}
      </div>

      {/* "Como aprimorar" reaproveita o popup que já está no ar, só com as skills que faltam PARA ESTA VAGA. */}
      <MelhorarSkills aberto={!!cursosDe} onFechar={() => setCursosDe(null)}
        skillsDaVaga={cursosDe?.matchFaltando} vagaId={cursosDe?.id} />
    </div>
  );
}
